/**
 * Where address search should look first. A BIAS handed to MapKit's search,
 * never a restriction — an org adding its first building in another city, or
 * another country, must still find it; the bias only decides what ranks first
 * among look-alikes ("380 St Kilda Rd" exists in more than one city).
 *
 * In order (Trav, 2026-09-30: "We are launching globally — I don't want
 * Australian locations to be preferential, just near their location"):
 *   1. the box around the org's own placed buildings (padded, so the suburb
 *      next door ranks too) — sensitive buildings never shape it;
 *   2. otherwise near the person: the place they chose to share with "Use my
 *      location" this session, else the rough city their browser's time zone
 *      names (`rough-location.ts` — no prompt, no network);
 *   3. otherwise no bias at all. No country is preferred, anywhere.
 * The centre is always rounded to ~0.1°: a search hint needs a neighbourhood,
 * never a building's or a person's exact spot.
 */

export interface SearchRegion {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
}

/** Where the person is, as precisely as they (or their time zone) said. */
export interface Near {
  latitude: number;
  longitude: number;
  /** `exact`: they shared it; `city`/`region`: read from the time zone. */
  precision: 'exact' | 'city' | 'region';
}

/** How wide a hint each kind of "near" is worth, in degrees. */
const NEAR_SPAN: Record<Near['precision'], number> = { exact: 0.4, city: 1, region: 40 };

/** A box around the org's own buildings never narrower than ~40 km. */
const MIN_SPAN_DEG = 0.4;

/** ~11 km — a search hint needs a neighbourhood, never an exact spot. */
const ROUND_DEG = 0.1;
const round = (v: number) => Math.round(v / ROUND_DEG) * ROUND_DEG;

export function searchRegion(
  buildings: {
    latitude: number | null | undefined;
    longitude: number | null | undefined;
    sensitive?: boolean;
  }[],
  near?: Near | null,
): SearchRegion | null {
  // A sensitive building (a refuge, a court) never shapes a query that leaves
  // for Apple (security review L2).
  const placed = buildings.filter(
    (b): b is { latitude: number; longitude: number; sensitive?: boolean } =>
      !b.sensitive && typeof b.latitude === 'number' && typeof b.longitude === 'number',
  );
  if (placed.length > 0) {
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLon = Infinity;
    let maxLon = -Infinity;
    for (const b of placed) {
      minLat = Math.min(minLat, b.latitude);
      maxLat = Math.max(maxLat, b.latitude);
      minLon = Math.min(minLon, b.longitude);
      maxLon = Math.max(maxLon, b.longitude);
    }
    // An estate spanning half the planet is not a hint worth giving — fall
    // through to where the person is instead.
    if (maxLon - minLon <= 60 && maxLat - minLat <= 60) {
      return {
        latitude: round((minLat + maxLat) / 2),
        longitude: round((minLon + maxLon) / 2),
        // Padded by half again on each side.
        latitudeDelta: Math.max(MIN_SPAN_DEG, (maxLat - minLat) * 2),
        longitudeDelta: Math.max(MIN_SPAN_DEG, (maxLon - minLon) * 2),
      };
    }
  }
  return near ? regionNear(near) : null;
}

/** A search hint around one place — what "Use my location" and the time zone give. */
export function regionNear(near: Near): SearchRegion {
  const span = NEAR_SPAN[near.precision];
  return {
    latitude: round(near.latitude),
    longitude: round(near.longitude),
    latitudeDelta: span,
    longitudeDelta: span,
  };
}

/** How wide a hint the place being set up is worth — the suburb and its neighbours. */
const ANCHOR_SPAN_DEG = 0.3;

/**
 * The ONE region every address search on the building screens uses (the
 * wizard's building and assembly-point fields, the building page's add form),
 * so no field is left searching the whole planet — the assembly-point field
 * was, and an address typed near a Melbourne building came back as UK streets
 * (Trav, 2026-10-01).
 *
 * In order: the building just placed (an assembly point is metres from its
 * building, never a country away), then the org's own buildings, then near the
 * person — `searchRegion`'s rules for the last two. A sensitive building never
 * shapes a query that leaves for Apple, so it is skipped as an anchor too. No
 * country is preferred, anywhere.
 */
export function addressSearchRegion({
  anchor,
  buildings,
  near,
}: {
  anchor?: { latitude: number; longitude: number; sensitive?: boolean } | null;
  buildings: Parameters<typeof searchRegion>[0];
  near?: Near | null;
}): SearchRegion | null {
  if (anchor && !anchor.sensitive) {
    return {
      latitude: round(anchor.latitude),
      longitude: round(anchor.longitude),
      latitudeDelta: ANCHOR_SPAN_DEG,
      longitudeDelta: ANCHOR_SPAN_DEG,
    };
  }
  return searchRegion(buildings, near);
}
